// src/services/uploadService.js
import { toast } from "react-toastify";
import { httpService } from "../../services/httpService";
import config from "../../config";

const DEFAULT_MODULE = "activity";
const DEFAULT_DOCTYPE = "media";

/**
 * Upload a single file to server and return either s3Key or fullUrl.
 * @param {File} file
 * @param {Object} opts
 * @param {"s3Key"|"fullUrl"} opts.returnType
 * @param {string} opts.module
 * @param {string} opts.docType
 * @returns {Promise<string>} s3Key or fullUrl
 */
export async function uploadFileToS3(
  file,
  { returnType = "s3Key", module = DEFAULT_MODULE, docType = DEFAULT_DOCTYPE } = {}
) {
  if (!(file instanceof File)) throw new Error("uploadFileToS3 requires a File");

  const formData = new FormData();
  formData.append("module", module);
  formData.append("docType", docType);
  formData.append("file", file);

  try {
    const resp = await httpService.post("/upload/fileUpload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      // If your httpService supports progress you can add onUploadProgress here.
    });

    const s3Key = resp?.data?.s3Key;
    if (!s3Key) throw new Error("Upload response missing s3Key");

    if (returnType === "fullUrl") {
      return `${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${s3Key}`;
    }
    return s3Key;
  } catch (err) {
    console.error("uploadFileToS3 error:", err);
    toast.error("Upload failed");
    throw err;
  }
}

/**
 * Upload multiple files in series (safer for throttling) or concurrently.
 * Returns array of s3Keys/fullUrls (keeps order of input files, errors produce nulls).
 */
export async function uploadMultipleFiles(files = [], options = {}) {
  if (!Array.isArray(files)) files = Array.from(files);
  const results = [];

  // Option A: serial upload (recommended for reliability)
  for (const f of files) {
    try {
      const key = await uploadFileToS3(f, options);
      results.push(key);
    } catch (e) {
      results.push(null);
    }
  }
  return results;

  // Option B: concurrent (uncomment if you want)
  // return Promise.all(files.map(f => uploadFileToS3(f, options).catch(() => null)));
}
